// Device-location check against POST /api/location/check — the client half of
// the cash-DFS physical-presence gate (used by LocationGate.vue on the verify
// wizard + DFS entry). Three platform branches, one server decision:
//   native embed w/ 'location' capability -> bridge LOCATION_GET (expo-location,
//     forwards android's mocked flag verbatim — the server's hard spoof signal)
//   everything else -> browser Geolocation API, 2-3 fixes via watchPosition so
//     the server can run its jitter analysis (spoofed values don't jitter)
// The server recomputes everything from the raw samples; we only ferry them.
// Client-only by design: check() is called from user-gesture handlers, which
// also keeps the browser permission prompt gesture-attributed (never on load).
//
// The fix collection itself (collectFixes) lives at module scope so the BMO
// venue picker (useVenueLookup) gathers samples exactly the same way — one
// implementation of the platform branches, two server routes.
import { useNativeBridge } from './useNativeBridge'

export type LocationPurpose = 'verify' | 'dfs_entry' | 'deposit' | 'withdrawal'
export type LocationStatus =
  | 'idle' | 'locating' | 'checking'
  | 'passed' | 'location_needed' | 'not_in_nh' | 'spoof_suspected' | 'error'

// what the server may answer; anything else is treated as an error so the gate
// always has a branch to render rather than showing nothing
const RESULT_STATUSES: readonly LocationStatus[] =
  ['passed', 'location_needed', 'not_in_nh', 'spoof_suspected'] as const

// eligible venues near the player, sent only on not_in_nh (name + miles, no address)
export interface NearbySite { site_id: number; name: string; distance_mi: number }

interface CheckOut {
  status: string
  reason_codes: string[]
  nearby_sites?: NearbySite[]
  expires_at: string | null
  checked_at: string | null
  ttl_sec: number
}

interface StatusOut { fresh: boolean; status: string; expires_at: string | null; checked_at: string | null }

const SAMPLES_WANTED = 3
const COLLECT_MS = 4500          // stop waiting for extra fixes after this
// The server treats fewer than two fixes as "no usable fix" (a single sample
// can't be jitter-checked, which is what catches a devtools/console override).
// A cold GPS can take most of COLLECT_MS to produce its first fix, so rather
// than fail the check we extend the window once for the second one.
const MIN_SAMPLES = 2
const COLLECT_GRACE_MS = 4500    // one extra wait, only if we're short of MIN
const FIX_TIMEOUT_MS = 10000     // browser per-fix timeout

export type Fix = { lat: number; lon: number; accuracy_m: number | null; ts_ms: number | null }
export type Collected =
  | { permission: 'granted'; samples: Fix[]; mocked: boolean | null }
  | { permission: 'denied' | 'unavailable' | 'timeout' | 'unsupported'; samples: []; mocked: null }
// what collectFixes hands back: the samples plus which platform path produced
// them (the server records `source`) and, native only, whether the OS will
// never ask again (canAskAgain === false -> the UI offers Open Settings)
export type CollectedFixes = Collected & { source: 'browser' | 'native'; deniedForever: boolean }

type Bridge = ReturnType<typeof useNativeBridge>

function collectBrowser(): Promise<Collected> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ permission: 'unsupported', samples: [], mocked: null })
      return
    }
    const samples: Fix[] = []
    let settled = false
    let watchId = 0
    let timer: any = 0
    let extended = false
    const finish = (out: Collected) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      navigator.geolocation.clearWatch(watchId)
      // copy: `samples` is the live buffer the watch pushes into, and a fix
      // delivered between clearWatch and the POST would otherwise ride along
      resolve({ ...out, samples: [...out.samples] })
    }
    const onDeadline = () => {
      if (samples.length >= MIN_SAMPLES) {
        finish({ permission: 'granted', samples, mocked: null })
        return
      }
      if (!extended) {
        // short of the minimum — give the second fix one more window before
        // giving up, instead of sending an unusable single sample
        extended = true
        timer = setTimeout(onDeadline, COLLECT_GRACE_MS)
        return
      }
      // still short: recoverable, the panel invites a retry
      finish({ permission: 'timeout', samples: [], mocked: null })
    }
    timer = setTimeout(onDeadline, COLLECT_MS)
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        samples.push({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy ?? null,
          ts_ms: pos.timestamp ?? null,
        })
        if (samples.length >= SAMPLES_WANTED) {
          finish({ permission: 'granted', samples, mocked: null })
        }
      },
      (err) => {
        const permission = err.code === 1 ? 'denied' : err.code === 3 ? 'timeout' : 'unavailable'
        finish({ permission, samples: [], mocked: null })
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: FIX_TIMEOUT_MS },
    )
  })
}

async function collectNative(bridge: Bridge): Promise<Collected & { deniedForever: boolean }> {
  try {
    const res = await bridge.getLocation({ samples: SAMPLES_WANTED, timeoutMs: FIX_TIMEOUT_MS })
    // same MIN_SAMPLES rule as the browser path — a shell that returns a
    // single fix gets the recoverable timeout state, not an unusable check
    if (res.status === 'ok' && (res.samples?.length ?? 0) >= MIN_SAMPLES) {
      return { permission: 'granted', samples: res.samples as Fix[], mocked: res.mocked ?? null, deniedForever: false }
    }
    if (res.status === 'ok') return { permission: 'timeout', samples: [], mocked: null, deniedForever: false }
    if (res.status === 'denied' || res.status === 'denied_forever') {
      return { permission: 'denied', samples: [], mocked: null, deniedForever: res.status === 'denied_forever' }
    }
    if (res.status === 'timeout') return { permission: 'timeout', samples: [], mocked: null, deniedForever: false }
    return { permission: 'unavailable', samples: [], mocked: null, deniedForever: false }
  } catch {
    // bridge timeout / no reply — treat like a device without a fix
    return { permission: 'unavailable', samples: [], mocked: null, deniedForever: false }
  }
}

// Gather 2-3 device fixes via whichever platform path applies. Client-only;
// call it from a user-gesture handler (the browser prompt must stay attributed).
export async function collectFixes(bridge: Bridge): Promise<CollectedFixes> {
  const useBridge = bridge.isNative.value && !!bridge.ctx.value.capabilities?.includes('location')
  if (useBridge) return { ...(await collectNative(bridge)), source: 'native' }
  return { ...(await collectBrowser()), source: 'browser', deniedForever: false }
}

// the device's IANA zone, one of the server's soft anti-spoof signals
export function clientTimezone(): string | null {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null } catch { return null }
}

export function useLocationCheck() {
  const api = useApi()
  const bridge = useNativeBridge()

  const status = ref<LocationStatus>('idle')
  const reasonCodes = ref<string[]>([])
  const nearbySites = ref<NearbySite[]>([])
  const expiresAt = ref<string | null>(null)
  const deniedForever = ref(false)   // native only: canAskAgain === false -> offer Open Settings

  async function check(purpose: LocationPurpose): Promise<LocationStatus> {
    status.value = 'locating'
    reasonCodes.value = []
    nearbySites.value = []
    expiresAt.value = null      // a re-check must not leave the previous pass's expiry
    deniedForever.value = false

    const collected = await collectFixes(bridge)
    deniedForever.value = collected.deniedForever

    status.value = 'checking'
    try {
      const out = await api.post<CheckOut>('/location/check', {
        purpose,
        source: collected.source,
        permission: collected.permission,
        samples: collected.samples,
        timezone: clientTimezone(),
        mocked: collected.mocked,
      })
      const s = out.status as LocationStatus
      status.value = RESULT_STATUSES.includes(s) ? s : 'error'
      reasonCodes.value = out.reason_codes || []
      nearbySites.value = out.nearby_sites || []
      expiresAt.value = out.expires_at
    } catch (e: any) {
      // 503 = provider trouble (retryable); 429 = rate-limited; both -> error state.
      // read every shape ofetch reports the status in (same as useVenueLookup)
      status.value = 'error'
      const code = e?.status ?? e?.statusCode ?? e?.response?.status
      if (code === 429) reasonCodes.value = ['RATE_LIMITED']
    }
    return status.value
  }

  async function hasFreshPass(): Promise<boolean> {
    try {
      const s = await api.get<StatusOut>('/location/status')
      return !!s.fresh
    } catch {
      return false
    }
  }

  function openSettings() {
    bridge.openLocationSettings()
  }

  return { status, reasonCodes, nearbySites, expiresAt, deniedForever, check, hasFreshPass, openSettings }
}
